import { getGUI, getOSM } from '../OverworldAnimation.js';

export default class OverworldAction {
    constructor({
        startAction = () => {},
        action = () => {},
        endAction = () => {},
        duration = 0,
        isAsync = false,
    }) {
        this.startAction = startAction;
        this.action = action;
        this.endAction = endAction;
        this.duration = duration;
        this.started = false;
        this.ended = false;
        this.cursorBuffer = 0; // Will be initialized in start() based on text speed
        this.stateMachine = getOSM();
        this.isAsync = isAsync;
        this.isExecuting = false;
        this.executionPromise = null;
    }

    start() {
        if (this.started) {
            return;
        }
        this.startAction();
        const osm = getOSM(); // Get latest state machine instance
        // Only reset cursor if there's no text currently displayed
        // This prevents clearing text when non-text actions start
        if (!osm.overworldText || osm.overworldText.trim() === '') {
            osm.cursor = 0;
        }
        // Initialize cursor buffer based on text speed setting
        const textSpeedBuffer = osm.getTextSpeedBuffer();
        this.cursorBuffer = textSpeedBuffer;
        this.started = true;
    }

    async execute() {
        this.start();

        // Handle async actions
        if (this.isAsync) {
            // If already executing, don't start again
            if (this.isExecuting) {
                return;
            }

            this.isExecuting = true;
            try {
                const result = await this.action();
                this.ended = result && this.duration <= 0;
            } catch (error) {
                console.error('Error executing async action:', error);
                this.ended = true;
            } finally {
                this.isExecuting = false;
            }
        } else {
            // Handle synchronous actions
            this.ended = this.action() && this.duration <= 0;
        }

        this.duration -= 1;

        const osm = getOSM(); // Get latest state machine instance

        // Update cursor buffer based on text speed setting
        const textSpeedBuffer = osm.getTextSpeedBuffer();
        const incrementAmount = osm.getTextSpeedIncrementAmount();

        this.cursorBuffer = Math.max(this.cursorBuffer - 1, 0);
        // Don't advance cursor if scrolling is waiting for user input
        const gui = getGUI();

        if (gui && gui.overworldText && osm.overworldText) {
            const isWaitingForScroll = gui.overworldText.waitingForUserInput() && gui.overworldText.canScroll;
            if (!gui.overworldText.isScrolling() && !isWaitingForScroll) {
                if (this.cursorBuffer === 0) {
                    osm.cursor += incrementAmount;
                    // Ensure cursor doesn't exceed text length
                    if (osm.overworldText && osm.cursor > osm.overworldText.length) {
                        osm.cursor = osm.overworldText.length;
                    }
                    // Reset buffer for next character(s)
                    this.cursorBuffer = textSpeedBuffer;
                }
            }
        } else {
            // If GUI or overworldText doesn't exist, advance cursor normally
            if (this.cursorBuffer === 0) {
                osm.cursor += incrementAmount;
                // Ensure cursor doesn't exceed text length
                if (osm.overworldText && osm.cursor > osm.overworldText.length) {
                    osm.cursor = osm.overworldText.length;
                }
                // Reset buffer for next character(s)
                this.cursorBuffer = textSpeedBuffer;
            }
        }

        this.end();
    }

    end() {
        if (!this.ended) {
            return;
        }
        this.endAction();
    }
}
